import { useMemo, type ReactNode } from 'react';
import {
  registeredLabel,
  useLiveRegistrationCounts,
  useMyLiveRegistrations,
} from '@/lib/live-registrations';
import { exerciseNavTarget } from '@/lib/exercise-route';
import { AccountSwitcher } from '@/components/admin/account-switcher';
import { DashboardTrousse } from '@/features/trousse/dashboard-trousse';
import { HelpTip } from '@/components/help-tip';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import { Button, Card, CardContent } from '@jeraisonne/ui';
import { ChevronRight, Radio } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useLaunchPending } from '@/hooks/use-launch-pending';
import { AvatarCircle } from '@/components/avatar-circle';
import { StudentHelpShortcuts } from '@/components/student-help-shortcuts';
import { skillcardStats, useStudentSkillcards } from '@/lib/cards/student-skillcards';
import { useMyEvaluations } from '@/lib/evaluations/api';
import { formatGradeNumber, weightedAverage } from '@/lib/evaluations/grades';
import { EvaluationsDashboardCard } from '@/features/evaluations/evaluations-dashboard-card';
import { AutomatismesCard } from '@/features/automatismes/automatismes-card';
import { ScratchTutorialCard } from '@/features/scratch-tutorial/scratch-tutorial-card';
import { useAutomatismesState } from '@/lib/automatismes';
import { StudentSkillcardsSection } from '@/features/skillcards/student-skillcards-section';
import { ProgrammeOffersSection } from '@/features/skillcards/programme-offers-section';
import {
  DiagnosticCard,
  DiagnosticPromptDialog,
} from '@/features/skill-diagnostic/diagnostic-dashboard';
import { useDiagnosticState } from '@/lib/skill-diagnostic';
import { MyYearCard } from '@/features/curriculum/my-year-card';
import { supabase } from '@/lib/supabase';
import { getActiveObjectives } from '@/lib/student-objectives/student-objectives';
import { getObjectivesProgress } from '@/lib/student-objectives/objective-progress';
import type { ObjectiveProgress } from '@/lib/student-objectives/objective-types';
import { MaClasseSection } from '@/components/student-objectives/ma-classe-section';
import { MonProfSection } from '@/components/student-objectives/mon-prof-section';
import { ObjectiveList } from '@/components/student-objectives/objective-list';
import { TutorInvitationBanner } from '@/components/tutor-invitation-banner';
import { UpcomingRendezVousCard } from '@/components/upcoming-rendezvous-card';
import { useMyUpcomingBookings } from '@/lib/booking/booking-slots';
import { useInProgressExercises } from '@/hooks/use-in-progress-exercises';
import { useStudentHomework } from '@/lib/student/homework';
import { buildTodayItems, type TodayItem } from '@/lib/student/today';
import { nextLiveForHome } from '@/lib/student/next-live';
import { useLiveEvents } from '@/features/news-tabs/use-live-events';
import { formatLiveEventDay, formatLiveEventTime } from '@/features/news-tabs/live-events';
import { StudentTodaySection } from '@/components/student-today-section';
import { CountUp } from '@/components/count-up';
import { SkillcardPromoBanner } from '@/features/skillcards/skillcard-promo-banner';

// ─── Label maps ───────────────────────────────────────────────────────────────

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));

// ─── Types ────────────────────────────────────────────────────────────────────

type Chapter = { id: string; title: string; subject: string; level: string; slug: string };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Bonjour';
  if (h < 18) return 'Bon après-midi';
  return 'Bonsoir';
}

// ─── Page entry ──────────────────────────────────────────────────────────────

export function StudentDashboardPage() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { pendingId, launch } = useLaunchPending();
  const studentId = user?.id ?? '';

  // ── XP + streak ──
  const { data: studentProfile } = useQuery({
    queryKey: ['student-profile', studentId],
    enabled: !!studentId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('student_profiles')
        .select('xp, streak_days, level, subjects')
        .eq('id', studentId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // Diagnostic de programme (0318) : créé ici s'il manque (onboarding / rentrée).
  const { data: diagnostic } = useDiagnosticState(studentId);
  // Automatismes (0323) : séries flash sur le programme en cours et antérieur.
  const { data: automatismes } = useAutomatismesState(studentId);
  // HORA cumulé de ses SKILLCARDS (valeur courante, érosion comprise — 0315).
  // Même requête que le bloc « Mes SKILLCARDS » plus bas (clé partagée).
  const { data: skillcards = [] } = useStudentSkillcards(studentId);
  const totalHora = skillcardStats(skillcards).totalXp;
  // Notes et évals saisies par l'élève (0321) : moyenne pondérée sur 20.
  const { data: evaluations } = useMyEvaluations(studentId);
  const average = weightedAverage(evaluations ?? []);
  const masteryById = useMemo(
    () =>
      new Map(skillcards.filter((c) => c.usage_count > 0).map((c) => [c.id, c.mastery] as const)),
    [skillcards],
  );

  // ── Taux de réussite global (sur toutes les étapes notées de l'élève) ──
  const { data: successStats } = useQuery({
    queryKey: ['success-rate', studentId],
    enabled: !!studentId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('attempt_steps')
        .select('was_correct, attempts!inner(student_id)')
        .eq('attempts.student_id', studentId)
        .not('was_correct', 'is', null);
      if (error) throw error;
      const total = data?.length ?? 0;
      const correct = data?.filter((s) => s.was_correct === true).length ?? 0;
      return { total, correct };
    },
  });

  const successRate =
    successStats && successStats.total > 0
      ? Math.round((successStats.correct / successStats.total) * 100)
      : null;

  // ── Objectives ──
  const { data: objectives = [] } = useQuery({
    queryKey: ['student-objectives', studentId],
    enabled: !!studentId,
    queryFn: () => getActiveObjectives(supabase, studentId),
  });

  const studentLevel = studentProfile?.level ?? null;
  const { data: progressList = [] } = useQuery({
    queryKey: [
      'objectives-progress',
      studentId,
      objectives.map((o) => o.id).join(','),
      studentLevel,
    ],
    enabled: objectives.length > 0,
    // Une requête pour tous les objectifs SKILLCARDS (migration 0514).
    queryFn: () => getObjectivesProgress(supabase, studentId, objectives, studentLevel),
  });

  const progressMap = new Map<string, ObjectiveProgress>(
    progressList.map((p) => [p.objectiveId, p]),
  );

  // Tri : deadlines les plus urgentes (= les plus proches) en premier ;
  // objectifs sans deadline en queue (ordre de création conservé).
  const sortedObjectives = useMemo(() => {
    // L'ancien objectif « Diagnostic » (0153) est remplacé par le diagnostic de
    // programme (0318) : il n'est plus affiché.
    return objectives
      .filter((o) => o.template.type !== 'diagnostic')
      .sort((a, b) => {
        const aDl = a.deadline ? new Date(a.deadline).getTime() : null;
        const bDl = b.deadline ? new Date(b.deadline).getTime() : null;
        if (aDl !== null && bDl !== null) return aDl - bDl;
        if (aDl !== null) return -1;
        if (bDl !== null) return 1;
        return 0;
      });
  }, [objectives]);

  // ── Navigation helpers ──
  function goToExercise(chapter: Chapter, exerciseId: string) {
    navigate({
      to: '/learn/$subject/$level/$chapter/$exercise',
      params: {
        subject: chapter.subject,
        level: chapter.level,
        chapter: chapter.slug,
        exercise: exerciseId,
      },
    });
  }

  async function handleGoToExerciseFromId(exerciseId: string): Promise<boolean> {
    const { data: ex } = await supabase
      .from('exercises')
      .select('chapter_id, chapters ( id, title, subject, level, slug )')
      .eq('id', exerciseId)
      .single();
    if (!ex) return false;
    const ch = ex.chapters as unknown as Chapter | null;
    // Exercice sans chapitre (SKILLCARD) : lecteur direct.
    navigate(exerciseNavTarget(exerciseId, ch));
    return true;
  }

  // Spinner sur le bouton cliqué jusqu'à l'ouverture de l'exercice.
  function startExercise(exerciseId: string) {
    void launch(exerciseId, () => handleGoToExerciseFromId(exerciseId)).catch(console.error);
  }

  const { data: upcomingRendezVous = [] } = useMyUpcomingBookings(studentId);

  // ── « À faire » : devoirs restants + reprise + diagnostic à faire ──
  const homework = useStudentHomework(studentId);
  const inProgress = useInProgressExercises(studentId);
  const todayItems = useMemo(
    () =>
      buildTodayItems({
        homework: homework.data ?? [],
        inProgress: inProgress.data ?? [],
        diagnostic,
      }),
    [homework.data, inProgress.data, diagnostic],
  );

  function launchToday(item: Extract<TodayItem, { kind: 'homework' | 'resume' }>) {
    void launch(item.key, () => {
      const exerciseId =
        item.kind === 'homework' ? item.homework.exerciseId : item.exercise.exerciseId;
      const chapter =
        item.kind === 'homework'
          ? item.homework.chapter
          : item.exercise.chapterSlug
            ? {
                subject: item.exercise.subject,
                level: item.exercise.level,
                slug: item.exercise.chapterSlug,
              }
            : null;
      if (!chapter) return handleGoToExerciseFromId(exerciseId);
      goToExercise({ id: '', title: '', ...chapter }, exerciseId);
      return true;
    }).catch(console.error);
  }

  // ── Live School : en cours, sinon le prochain dans les 7 jours ──
  const { data: liveEvents } = useLiveEvents(studentProfile?.level ?? null);
  const nextLive = nextLiveForHome(liveEvents);
  // inscription au prochain live + nombre d'inscrits (signalement du 31/10/2026 : « comment on sait qu'on y
  // participe ? »)
  const { data: myLiveRegistrations } = useMyLiveRegistrations(nextLive ? (user?.id ?? '') : '');
  const { data: liveCounts } = useLiveRegistrationCounts(nextLive ? [nextLive.event.id] : []);
  const liveRegistered = !!nextLive && !!myLiveRegistrations?.has(nextLive.event.id);
  const liveCount = nextLive ? liveCounts?.get(nextLive.event.id) : undefined;

  if (!profile || !user) return null;

  const level = studentProfile?.level;
  const firstName = profile.display_name?.trim().split(/\s+/)[0] ?? '';
  const streak = studentProfile?.streak_days ?? 0;
  // Pas de rangée de zéros : les chiffres n'apparaissent qu'une fois qu'il y a quelque chose.
  const hasStats =
    (studentProfile?.xp ?? 0) > 0 || successRate !== null || totalHora > 0 || average != null;
  // Diagnostic à faire / en cours → déjà dans « À faire » ; terminé → carte de suivi (rapport).
  const diagnosticFinished =
    diagnostic?.current?.status === 'completed' || diagnostic?.current?.status === 'abandoned';

  return (
    <div className="mx-auto max-w-5xl space-y-7 px-4 py-6 sm:space-y-8 sm:py-8 md:px-8">
      {/* ── En-tête : qui, quel jour, sa série ── */}
      {/* ── En-tête : la date (et le changement de compte) en haut ; dessous, qui, sa série, et sa
          trousse à droite — elle ne serre plus le prénom ── */}
      <header className="space-y-1.5" data-testid="student-header">
        <div className="flex min-h-[2.75rem] items-center justify-between gap-3">
          <p className="font-display text-ink-500 text-xs font-semibold uppercase tracking-[.14em]">
            {new Date().toLocaleDateString('fr-FR', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            })}
          </p>
          <AccountSwitcher />
        </div>
        <div className="flex items-center gap-3">
          <AvatarCircle
            name={profile.display_name}
            src={profile.avatar_url}
            size="lg"
            className="ocean:ring-[#0f2a63] shrink-0 shadow-[0_4px_12px_rgba(5,102,242,0.18)] ring-2 ring-white"
          />
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-ink-900 break-words text-2xl font-extrabold tracking-tight sm:text-3xl">
              {greeting()}
              {firstName ? `, ${firstName}` : ''}&nbsp;!
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {level && (
                <span className="bg-primary-100 text-primary-700 rounded-full px-2.5 py-0.5 text-xs font-semibold">
                  {LEVEL_LABEL[level]}
                </span>
              )}
              {streak > 0 && (
                <span
                  className="ocean:bg-[#3a2410] ocean:text-[#fdba74] rounded-full bg-[#ffedd5] px-2.5 py-0.5 text-xs font-bold text-[#9a3412]"
                  data-testid="streak-chip"
                >
                  🔥 {streak} jour{streak > 1 ? 's' : ''} d’affilée
                </span>
              )}
            </div>
          </div>
          <DashboardTrousse />
        </div>
      </header>
      <SkillcardPromoBanner to="/student/progression" ctaLabel="Voir mon année" />

      {/* ── Chiffres, juste sous l'avatar (pas de rangée de zéros pour un nouvel élève) ── */}
      {hasStats && (
        <div className="grid grid-cols-4 gap-1.5 sm:gap-3">
          <StatCard
            icon="⭐"
            tone="bg-[#fef3c7] ocean:bg-[#433410]"
            value={<CountUp value={studentProfile?.xp ?? 0} />}
            label="Points XP"
            help="xp"
          />
          <StatCard
            icon="🎯"
            tone="bg-[#ffe4e6] ocean:bg-[#4a1a2a]"
            value={
              successRate !== null ? <CountUp value={successRate} format={(n) => `${n}%`} /> : '—'
            }
            label="Réussite"
            help="reussite"
          />
          <StatCard
            icon="💎"
            tone="bg-[#ede9fe] ocean:bg-[#33215e]"
            value={<CountUp value={totalHora} />}
            label="HORA"
            help="hora"
          />
          <StatCard
            to="/student/evaluations"
            help="moyenne"
            icon="📝"
            tone="bg-[#dcfce7] ocean:bg-[#0f3b2c]"
            value={
              average == null ? (
                '—'
              ) : (
                <>
                  <CountUp
                    value={Math.round(average * 10) / 10}
                    decimals={1}
                    format={formatGradeNumber}
                  />
                  <span className="text-ink-500 text-xs font-bold sm:text-sm">/20</span>
                </>
              )
            }
            label="Moyenne"
          />
        </div>
      )}

      {/* ── SKILLCARDS, toujours juste sous les chiffres ── */}
      <StudentSkillcardsSection studentId={studentId} />

      {/* ── Invitations tuteur en attente (action requise) ── */}
      <TutorInvitationBanner studentId={studentId} />

      {/* ── À faire : devoirs, reprise, diagnostic ── */}
      <StudentTodaySection
        items={todayItems}
        isLoading={homework.isLoading || inProgress.isLoading}
        pendingKey={pendingId}
        onLaunch={launchToday}
      />

      {/* ── Rendez-vous à venir ── */}
      {upcomingRendezVous.length > 0 && (
        <section className="space-y-2" aria-labelledby="next-sessions-title">
          <div className="flex items-center justify-between gap-2">
            <h2 id="next-sessions-title" className="font-display text-ink-900 text-lg font-bold">
              {upcomingRendezVous.length > 1 ? 'Mes prochaines séances' : 'Ma prochaine séance'}
            </h2>
            <Link
              to="/student/rendez-vous"
              className="text-primary-600 inline-flex min-h-[2.75rem] items-center gap-1 text-sm font-semibold"
            >
              Rendez-vous <ChevronRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>
          {upcomingRendezVous.map((booking) => (
            <UpcomingRendezVousCard
              key={booking.reservationId}
              item={{
                reservationId: booking.reservationId,
                otherPartyName: booking.tutorName,
                startsAt: booking.startsAt,
                endsAt: booking.endsAt,
                kind: booking.kind,
              }}
            />
          ))}
        </section>
      )}

      {/* ── Live School de son niveau (en cours ou dans les 7 jours) ── */}
      {nextLive && level && (
        <Link
          to="/live/$niveau"
          params={{ niveau: level }}
          search={{ defi: nextLive.event.id }}
          className="border-primary-200 bg-primary-50/60 ocean:bg-white/5 flex min-h-[2.75rem] items-center gap-3 rounded-2xl border p-4 active:scale-[0.98]"
          data-testid="live-tile"
        >
          <span
            className={
              nextLive.live
                ? 'flex h-11 w-11 shrink-0 animate-pulse items-center justify-center rounded-xl bg-[#ef4444] text-white'
                : 'bg-primary-600 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white'
            }
          >
            <Radio className="h-5 w-5" aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="text-ink-900 block font-semibold">
              {nextLive.live ? 'Live School en direct' : 'Prochain Live School'}
            </span>
            <span className="text-ink-600 block text-sm first-letter:uppercase">
              {nextLive.live
                ? 'Rejoins la partie maintenant'
                : `${formatLiveEventDay(nextLive.event.starts_at)} à ${formatLiveEventTime(nextLive.event.starts_at)}`}
            </span>
            {(liveRegistered || liveCount !== undefined) && (
              <span className="text-ink-600 mt-0.5 block text-sm" data-testid="live-tile-status">
                {[
                  liveRegistered
                    ? '✓ Tu participes'
                    : !nextLive.live
                      ? 'Pas encore inscrit·e'
                      : null,
                  liveCount !== undefined ? registeredLabel(liveCount) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            )}
          </span>
          <ChevronRight className="text-ink-400 h-5 w-5 shrink-0" aria-hidden />
        </Link>
      )}

      {/* ── Notes et évals : prochaine échéance + SKILLCARDS à réviser ── */}
      <EvaluationsDashboardCard evaluations={evaluations} masteryById={masteryById} />

      {/* ── S'entraîner et suivre sa progression ── */}
      <section className="space-y-3" aria-labelledby="practice-title">
        <h2 id="practice-title" className="font-display text-ink-900 text-lg font-bold">
          M’entraîner
        </h2>
        <ScratchTutorialCard />
        <AutomatismesCard state={automatismes} />
        <MyYearCard />
        {diagnosticFinished && <DiagnosticCard state={diagnostic} />}
      </section>
      <DiagnosticPromptDialog state={diagnostic} />

      {/* ── Objectifs (le retrait se fait depuis l'objectif, avec confirmation) ── */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-ink-900 text-lg font-bold">Mes objectifs</h2>
          {sortedObjectives.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate({ to: '/student/objectives' })}
            >
              + Ajouter
            </Button>
          )}
        </div>
        <ObjectiveList objectives={sortedObjectives} progressByObjectiveId={progressMap} />
      </section>

      {/* ── Mon prof particulier : envoyer un exercice, fichiers, discussions ── */}
      <MonProfSection
        studentId={studentId}
        onStartExercise={startExercise}
        pendingExerciseId={pendingId}
      />

      {/* ── Ma classe ── */}
      <MaClasseSection
        studentId={studentId}
        onStartExercise={startExercise}
        pendingExerciseId={pendingId}
      />

      {/* ── Besoin d'aide ? ── */}
      <StudentHelpShortcuts />

      {/* ── Pack du moment + programme complet (s'il ne les a pas déjà) ── */}
      <ProgrammeOffersSection userId={studentId} level={level} />
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({
  icon,
  tone,
  value,
  label,
  shortLabel,
  to,
  help,
}: {
  icon: string;
  tone: string;
  value: number | ReactNode;
  label: string;
  /** Libellé court sur mobile (4 tuiles sur une ligne à 375 px). */
  shortLabel?: string;
  /** Tuile cliquable (page de détail). */
  to?: '/student/evaluations';
  /** Aide « ? » en bas à droite (`faq_items.context_key`, migration 0338). */
  help?: string;
}) {
  const card = (
    <Card className="border-ink-100 h-full">
      <CardContent className="flex h-full flex-col items-center gap-1 px-1 pb-6 pt-2 text-center sm:flex-row sm:items-center sm:gap-3 sm:p-3 sm:pr-6 sm:text-left">
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-sm sm:h-10 sm:w-10 sm:rounded-xl sm:text-xl ${tone}`}
          aria-hidden="true"
        >
          {icon}
        </span>
        <div className="min-w-0">
          <p className="font-display text-ink-900 text-sm font-extrabold leading-tight sm:text-xl">
            {typeof value === 'number' ? value.toLocaleString('fr-FR') : value}
          </p>
          {shortLabel ? (
            <p className="text-ink-500 text-xs leading-tight">
              <span className="sm:hidden" aria-hidden="true">
                {shortLabel}
              </span>
              <span className="sr-only sm:not-sr-only">{label}</span>
            </p>
          ) : (
            <p className="text-ink-500 text-xs leading-tight">{label}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
  // La pastille « ? » est À CÔTÉ du lien (jamais un bouton dans un lien).
  return (
    <div className="relative h-full">
      {to ? (
        <Link to={to} className="block h-full active:scale-[0.98]">
          {card}
        </Link>
      ) : (
        card
      )}
      {help && (
        <HelpTip
          topic={help}
          label={label}
          className="absolute bottom-0 right-0 sm:bottom-0.5 sm:right-0.5"
        />
      )}
    </div>
  );
}
